"use client";

import { signOut } from "@/lib/actions";
import { createClient } from "@/lib/supabase/client";
import { checkAdminPermissions, isExpiredSessionError } from "@/lib/utils/auth-helpers";
import type { User } from "@supabase/supabase-js";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Hook to manage header authentication state
 * Handles user session, admin permissions, and logout
 */
export function useHeaderAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();
  const router = useRouter();
  const authChecked = useRef(false);
  const supabase = useMemo(() => createClient(), []);

  // Helper function to clear user state
  const clearUserState = () => {
    setUser(null);
    setIsAdmin(false);
  };

  // Helper function to handle successful user session
  const handleUserSession = async (userData: User) => {
    const isAdminUser = await checkAdminPermissions(supabase, userData.id);
    setIsAdmin(isAdminUser);
    setUser(userData);
  };

  // Helper function to handle auth errors
  const handleAuthError = async (error: unknown) => {
    if (isExpiredSessionError(error)) {
      // Auth session expired; sign out silently
      await supabase.auth.signOut();
    } else {
      console.error("Error fetching user:", error);
    }
    clearUserState();
  };

  // Get user data on component mount with improved error handling
  useEffect(() => {
    async function getUser() {
      // Allow re-checking auth state on pathname changes
      if (authChecked.current && pathname === "/") {
        authChecked.current = false;
      }
      if (authChecked.current) return;

      setIsLoading(true);
      try {
        // First, try to get the session without refreshing
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData?.session?.user) {
          await handleUserSession(sessionData.session.user);
        } else {
          // No session found, user is not logged in
          clearUserState();
        }
      } catch (error) {
        await handleAuthError(error);
      } finally {
        setIsLoading(false);
        authChecked.current = true;
      }
    }

    getUser();
  }, [supabase, pathname]);

  // Handle logout with improved error handling
  const handleLogout = async () => {
    try {
      setIsLoading(true);

      // Use the server action for sign out
      const result = await signOut();

      if (result.success) {
        // Clear local auth state immediately (before navigation)
        // This ensures the header updates immediately even if router.push is a no-op
        clearUserState();
        // Clear Supabase client session
        await supabase.auth.signOut();
        // Reset auth check flag to allow re-checking auth state
        authChecked.current = false;

        // Navigate to home page using Next.js router for client-side navigation
        // Note: If a hard refresh is truly required (e.g., to clear all client-side state),
        // use window.location.href = "/" instead and document the specific reason
        router.push("/");
      }
    } catch (error) {
      console.error("Error during logout:", error);

      // Even if there's an error with the server action, try to sign out directly
      try {
        await supabase.auth.signOut();
      } catch (supabaseError) {
        console.error("Supabase signOut error:", supabaseError);
      }

      // Clear local state regardless of errors
      clearUserState();
      authChecked.current = false;

      // Navigate to home regardless of errors
      // Note: If a hard refresh is truly required (e.g., to clear all client-side state),
      // use window.location.href = "/" instead and document the specific reason
      router.push("/");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    isAdmin,
    isLoading,
    handleLogout,
  };
}
